<template>
  <v-dialog :value="value" max-width="600" persistent @input="$emit('input', $event)">
    <v-card class="rounded-lg" v-if="atraso">
      <v-card-title class="primary white--text py-3 px-4 d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon color="white" class="mr-2">mdi-clock-alert-outline</v-icon>
          <span class="text-h6 font-weight-bold">Detalhes da Justificativa de Atraso</span>
        </div>
        <v-chip small :color="corStatus(atraso.status)" class="font-weight-bold white--text">
          {{ formatarStatus(atraso.status) }}
        </v-chip>
      </v-card-title>

      <v-card-text class="pt-4 pb-2 px-4">
        <v-card outlined class="mb-4 grey lighten-5">
          <v-card-text class="py-3">
            <div v-if="atraso.colaborador" class="subtitle-1 font-weight-bold primary--text mb-1">
              {{ atraso.colaborador.name }}
            </div>
            <v-row dense class="caption">
              <v-col cols="6">
                Data do Atraso:
                <strong class="primary--text d-block">{{ formatarData(atraso.data) }}</strong>
              </v-col>
              <v-col cols="6">
                Enviado em:
                <strong class="grey--text text--darken-3 d-block">
                  {{ formatarDataHora(atraso.created_at) }}
                </strong>
              </v-col>
              <v-col cols="4">
                Previsto:
                <strong class="d-block">{{ atraso.hora_prevista || "—" }}</strong>
              </v-col>
              <v-col cols="4">
                Registrado:
                <strong class="error--text d-block">{{ atraso.hora_registrada || "—" }}</strong>
              </v-col>
              <v-col cols="4">
                Tempo de Atraso:
                <strong class="warning--text d-block">
                  {{ atraso.tempo_atraso || formatarMinutos(atraso.minutos_atraso) }}
                </strong>
              </v-col>
            </v-row>

            <v-divider class="my-2"></v-divider>

            <div class="caption font-weight-bold grey--text">Motivo Informado:</div>
            <div class="body-2 font-italic grey--text text--darken-3 mt-1">
              "{{ atraso.motivo }}"
            </div>

            <div v-if="atraso.anexo" class="mt-3">
              <v-btn
                outlined
                color="primary"
                small
                class="font-weight-bold"
                :loading="abrindoAnexo"
                @click="verAnexo"
              >
                <v-icon left small>mdi-paperclip</v-icon>
                Ver Anexo
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <template v-if="atraso.status !== 'pendente'">
          <v-card outlined class="mb-3">
            <v-card-text class="py-2">
              <div class="caption font-weight-bold grey--text">Resultado da Avaliação:</div>
              <v-row dense class="caption mt-1">
                <v-col cols="6">
                  Avaliado por: <strong>{{ atraso.avaliador?.name || "—" }}</strong>
                </v-col>
                <v-col cols="6">
                  Data da Análise: <strong>{{ formatarDataHora(atraso.avaliado_em) }}</strong>
                </v-col>
              </v-row>
              <div v-if="atraso.observacao_avaliador" class="mt-2">
                <div class="caption font-weight-bold grey--text">Parecer do Avaliador:</div>
                <div class="body-2 font-italic grey lighten-4 pa-2 rounded mt-1 border">
                  "{{ atraso.observacao_avaliador }}"
                </div>
              </div>
            </v-card-text>
          </v-card>
        </template>

        <template v-if="podeAvaliar">
          <v-divider class="my-3"></v-divider>
          <div class="subtitle-2 font-weight-bold primary--text mb-2">Avaliar Justificativa</div>
          <v-textarea
            v-model="observacao"
            label="Observação / Parecer do Avaliador (obrigatório para rejeitar)"
            placeholder="Digite suas considerações ou motivo..."
            outlined
            dense
            rows="3"
            counter="500"
            :disabled="!!avaliando"
            :rules="[(v) => !v || v.length <= 500 || 'Máximo 500 caracteres']"
          ></v-textarea>
        </template>
        <template v-else-if="modoGestor && atraso.status === 'pendente'">
          <v-alert type="info" text dense class="mb-0">
            Você não pode avaliar uma justificativa que você mesmo enviou.
          </v-alert>
        </template>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="py-3 px-4 bg-grey lighten-4">
        <v-btn text color="grey" :disabled="!!avaliando" @click="fechar">Fechar</v-btn>
        <v-spacer></v-spacer>
        <template v-if="podeAvaliar">
          <v-btn
            color="error"
            class="font-weight-bold mr-2"
            :loading="avaliando === 'rejeitado'"
            :disabled="!!avaliando || observacao.length > 500 || observacao.trim().length < 3"
            @click="avaliar('rejeitado')"
          >
            <v-icon left small>mdi-close-circle</v-icon>
            Rejeitar
          </v-btn>
          <v-btn
            color="success"
            class="font-weight-bold"
            :loading="avaliando === 'aprovado'"
            :disabled="!!avaliando || observacao.length > 500"
            @click="avaliar('aprovado')"
          >
            <v-icon left small>mdi-check-circle</v-icon>
            Aprovar
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AtrasoJustificadoDetailDialog",
  props: {
    value: { type: Boolean, default: false },
    atraso: { type: Object, default: null },
    // true na tela do gestor: permite avaliar e usa a rota de anexo administrativa
    modoGestor: { type: Boolean, default: false },
  },
  data() {
    return {
      observacao: "",
      avaliando: null,
      abrindoAnexo: false,
    };
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
    podeAvaliar() {
      if (!this.modoGestor || !this.atraso || this.atraso.status !== "pendente") return false;
      return this.user?.id !== this.atraso.colaborador_id;
    },
  },
  watch: {
    value(aberto) {
      if (aberto) this.observacao = "";
    },
  },
  methods: {
    fechar() {
      this.$emit("input", false);
    },
    corStatus(status) {
      return (
        { pendente: "amber darken-2", aprovado: "success", rejeitado: "error" }[status] || "grey"
      );
    },
    formatarStatus(status) {
      return (
        { pendente: "Pendente", aprovado: "Aprovada", rejeitado: "Rejeitada" }[status] || status
      );
    },
    formatarData(data) {
      if (!data) return "—";
      const [ano, mes, dia] = String(data).split("T")[0].split("-");
      return `${dia}/${mes}/${ano}`;
    },
    formatarDataHora(data) {
      if (!data) return "—";
      return new Date(data).toLocaleString("pt-BR");
    },
    formatarMinutos(minutos) {
      if (!minutos) return "00h00m";
      const h = Math.floor(minutos / 60);
      const m = minutos % 60;
      return `${String(h).padStart(2, "0")}h${String(m).padStart(2, "0")}m`;
    },
    async verAnexo() {
      try {
        this.abrindoAnexo = true;
        const service = this.$services.colaboradorPontosService;
        if (this.modoGestor) {
          await service.adminAbrirAnexoAtraso(this.atraso.id);
        } else {
          await service.abrirMeuAnexoAtraso(this.atraso.id);
        }
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.abrindoAnexo = false;
      }
    },
    async avaliar(status) {
      try {
        this.avaliando = status;
        await this.$services.colaboradorPontosService.adminAvaliarAtrasoJustificado(
          this.atraso.id,
          {
            status,
            observacao_avaliador: this.observacao || null,
          }
        );
        this.$toast.success(
          status === "aprovado" ? "Justificativa aprovada!" : "Justificativa rejeitada."
        );
        this.$emit("avaliado");
        this.fechar();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.avaliando = null;
      }
    },
  },
};
</script>
